import type { IAuthProvider } from "./types";
import type { User } from "../types/chat";
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
  getLocalStorageJSON,
  setLocalStorageJSON,
} from "@common";
import { getEnvironmentConfig } from "./environment";
import { STORAGE_KEYS } from "@config/app.config";

const AUTH_TOKEN_KEY = "chat_auth_token";
const AUTH_USER_KEY = "chat_auth_user";

export class StandaloneAuthProvider implements IAuthProvider {
  private listeners: Set<(user: User | null) => void> = new Set();

  getCurrentUser(): User | null {
    const storedUser = getLocalStorageJSON<User>(AUTH_USER_KEY);
    if (storedUser) {
      return storedUser;
    }

    const token = this.getToken();
    if (token) {
      try {
        const user = this.createUserFromToken(token);
        setLocalStorageJSON(AUTH_USER_KEY, user);
        return user;
      } catch (error) {
        console.error("Failed to create user from token:", error);
      }
    }

    return null;
  }

  getToken(): string | null {
    return (
      getLocalStorage(AUTH_TOKEN_KEY) ||
      getLocalStorage(STORAGE_KEYS.AUTH_TOKEN)
    );
  }

  private getApiBaseUrl(): string {
    const config = getEnvironmentConfig();
    const protocol = config.environment === "local" ? "http" : "https";
    return `${protocol}://${config.apiHost}/api`;
  }

  private decodeJWT(
    token: string
  ): { userId?: number; username?: string; [key: string]: unknown } | null {
    try {
      const parts = token.split(".");
      if (parts.length !== 3) {
        return null;
      }
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Failed to decode JWT:", error);
      return null;
    }
  }

  private createUserFromToken(token: string): User {
    const decoded = this.decodeJWT(token);
    const userId = decoded?.userId?.toString() || "unknown";
    const username =
      (typeof decoded?.username === "string" ? decoded.username : null) ||
      (typeof decoded?.displayName === "string" ? decoded.displayName : null) ||
      "User";

    return {
      id: userId,
      name: username,
      avatar: "",
      presence: "online" as const,
      role: (decoded?.roles as string[])?.includes("ADMIN")
        ? "admin"
        : "member",
      channels: [],
    };
  }

  async login(
    username: string,
    password: string,
    onError?: (message: string) => void
  ): Promise<{ user: User; token: string }> {
    const baseUrl = this.getApiBaseUrl();
    const loginUrl = `${baseUrl}/auth/login`;

    try {
      const response = await fetch(loginUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const responseData = await response.json();

      // If response has 'code' field and code is not 0, show error and throw
      if (responseData.code !== undefined && responseData.code !== 0) {
        const errorMessage = responseData.message || "Đăng nhập thất bại. Vui lòng thử lại.";
        if (onError) {
          onError(errorMessage);
        }
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        const errorMessage =
          responseData?.message ||
          responseData?.data?.message ||
          `Login failed: ${response.status} ${response.statusText}`;
        throw new Error(errorMessage);
      }

      let tokenData: {
        accessToken?: string;
        refreshToken?: string;
        tokenType?: string;
        platform?: string;
        token?: string; // Legacy format
        user?: User; // Legacy format
      };

      console.log("responseData", responseData);

      if (responseData.data && typeof responseData.data === "object") {
        tokenData = responseData.data;
      } else if (responseData.accessToken || responseData.token) {
        tokenData = responseData;
      } else {
        throw new Error("Invalid response format from server");
      }

      const token = tokenData.accessToken || tokenData.token;

      if (!token) {
        console.error("Login response data:", tokenData);
        throw new Error("Invalid response from server: no token found");
      }

      console.log("Token received:", token.substring(0, 20) + "...");

      let user: User;
      if (tokenData.user) {
        user = tokenData.user;
      } else {
        user = this.createUserFromToken(token);
        console.log("User created from token:", user);
      }

      setLocalStorage(AUTH_TOKEN_KEY, token);
      setLocalStorage(STORAGE_KEYS.AUTH_TOKEN, token);
      setLocalStorageJSON(AUTH_USER_KEY, user);
      console.log("Token and user stored in localStorage");

      if (tokenData.refreshToken) {
        setLocalStorage("chat_refresh_token", tokenData.refreshToken);
      }

      this.notifyListeners(user);
      console.log("Listeners notified");

      return { user, token };
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Login failed. Please try again.");
    }
  }

  logout(): void {
    removeLocalStorage(AUTH_TOKEN_KEY);
    removeLocalStorage(STORAGE_KEYS.AUTH_TOKEN);
    removeLocalStorage(AUTH_USER_KEY);

    this.notifyListeners(null);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  onAuthChange(callback: (user: User | null) => void): () => void {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  private notifyListeners(user: User | null): void {
    this.listeners.forEach((listener) => listener(user));
  }
}
